import React, { memo, useCallback, useContext } from 'react'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import AllowCommonUiInTestContext from '@signal/hoc/withCommonSnapshotsOnly/Context'
import { isNonCommonUiTest } from '@signal/utils/env/isTest'
import renderIf from '@signal/utils/renderIf'
import renderSwitch from '@signal/utils/renderSwitch'
import { ButtonNew as Button } from '../Button'
import Typography from '../Typography'
import { DescriptionWrapper, PromptDialog } from './styles'
import { ModalPromptChoiceType, ModalPromptPropsType } from './types'

export const ModalPrompt: React.FC<ModalPromptPropsType> = ({
  buttonVariant,
  choices = [],
  disabledChoices,
  IconComponent,
  DescriptionComponent,
  defaultChoiceId,
  descriptionText,
  onChoice,
  onDismiss,
  showPrompt,
  title,
  zIndexOverride = 100000,
  ...rest
}) => {
  const allowCommonUiInTest = useContext(AllowCommonUiInTestContext)

  const IconComponentUse = (IconComponent ??
    React.Fragment) as React.ElementType
  const handleChoice = useCallback(
    (choice: ModalPromptChoiceType) => (): void => {
      const { choiceId } = choice
      if (onChoice) {
        if (onChoice(choiceId) && onDismiss) {
          onDismiss(choiceId)
        }
      }
    },
    [onChoice, onDismiss]
  )

  const handleDefaultChoiceRef = useCallback(
    (ref: React.ElementRef<'button'>): void => {
      if (ref) {
        ref.focus()
      }
    },
    []
  )

  return renderIf(
    isNonCommonUiTest() && !allowCommonUiInTest,
    () =>
      // only render the description component, provided by the app, if this is not a common-ui unit test
      // @ts-ignore
      renderIf(Boolean(DescriptionComponent), () => <DescriptionComponent />),
    () => (
      <PromptDialog
        open={showPrompt}
        $zIndexOverride={zIndexOverride}
        {...rest}
      >
        <DialogTitle>{title || ''}</DialogTitle>
        <DialogContent>
          {renderSwitch([
            {
              condition: Boolean(DescriptionComponent),
              component: DescriptionComponent
            },
            {
              condition: Boolean(descriptionText),
              component: () => (
                <DialogContentText>
                  <DescriptionWrapper>
                    <IconComponentUse />
                    <Typography variant="subtitle2">
                      {descriptionText}
                    </Typography>
                  </DescriptionWrapper>
                </DialogContentText>
              )
            }
          ])}
        </DialogContent>
        <DialogActions>
          {choices.map((choiceCur: ModalPromptChoiceType) => {
            const { choiceId, buttonText } = choiceCur
            const isDefaultChoice = defaultChoiceId === choiceId
            return (
              <Button
                disableRipple
                className={`modal-choice-${choiceId}`}
                disabled={disabledChoices?.has(choiceId)}
                key={choiceId}
                ref={isDefaultChoice ? handleDefaultChoiceRef : undefined}
                onClick={handleChoice(choiceCur)}
                variant={
                  buttonVariant || (isDefaultChoice ? 'contained' : 'text')
                }
              >
                {buttonText}
              </Button>
            )
          })}
        </DialogActions>
      </PromptDialog>
    )
  ) as React.ReactElement
}

export default memo(ModalPrompt)
